import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Puts the buyer back where they were.
 *
 * The browser restores scroll on its own for a document navigation, and cannot for a client-side
 * one — the new page renders at whatever height it happens to have before the data arrives, so the
 * position it would restore to does not exist yet. The result is the single most irritating thing
 * about a single-page marketplace: open the ninth car in the results, press back, and land at the
 * top of a list you had spent a minute scrolling through.
 *
 * So positions are remembered per history entry, and restoration is deferred until the page has
 * actually grown tall enough to hold the offset — polled briefly rather than guessed at with a
 * fixed timeout, because how long the results take to arrive is not knowable in advance.
 */
export function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, number>());

  // The key is the history entry, not the path, so two visits to the same search remember their own
  // positions and a back navigation restores the one it came from.
  const key = location.key;

  const previous = useRef({ pathname: location.pathname, search: location.search });

  useEffect(() => {
    const remember = () => positions.current.set(key, window.scrollY);

    window.addEventListener('scroll', remember, { passive: true });

    return () => {
      remember();
      window.removeEventListener('scroll', remember);
    };
  }, [key]);

  useEffect(() => {
    const from = previous.current;
    previous.current = { pathname: location.pathname, search: location.search };

    /*
      A refinement rather than a destination: same page, different query string.

      Filters live in the URL and are pushed rather than replaced, deliberately, so the back button
      steps back through them one at a time. That makes every tick of a checkbox a PUSH — and
      treating it as arrival somewhere new would throw the buyer to the top of the list on every
      single one, which is exactly the thing this component exists to prevent.
    */
    if (
      navigationType === 'PUSH' &&
      from.pathname === location.pathname &&
      from.search !== location.search
    ) {
      return;
    }

    // A REPLACE never moves the page either: nothing about the destination changed.
    if (navigationType === 'REPLACE') {
      return;
    }

    // POP is a back or forward: the only case where restoring a remembered offset is right.
    const target = navigationType === 'POP' ? (positions.current.get(key) ?? 0) : 0;

    if (target === 0) {
      // Explicitly instant. `scroll-behavior: smooth` on `html` is there for in-page anchors, and
      // the two-argument form of `scrollTo` obeys it — so arriving on a page was animating the
      // scroll from wherever the last one ended, several hundred milliseconds of the whole document
      // sliding underneath the fixed bars that have to re-blur whatever moves behind them.
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    let frames = 0;

    const restore = () => {
      // Give up after about a second. Beyond that the content is not coming, and jumping the page
      // long after the visitor has started reading is worse than not restoring at all.
      if (frames > 60) {
        return;
      }

      frames += 1;

      if (document.documentElement.scrollHeight >= target + window.innerHeight) {
        window.scrollTo({ top: target, behavior: 'instant' });
        return;
      }

      requestAnimationFrame(restore);
    };

    requestAnimationFrame(restore);
  }, [key, navigationType, location.pathname, location.search]);

  return null;
}
